import { useCallback, useSyncExternalStore } from 'react';
import {
  getPresenceSnapshot,
  subscribePresence,
  type PresenceState,
} from '@/lib/presenceStore';
import { pluralRu } from '@/utils/russianPlural';

const EMPTY_PRESENCE: PresenceState = { online: false, lastSeenAt: null };

function formatPresenceLabel(state: PresenceState): string {
  if (state.online) return 'онлайн';
  if (!state.lastSeenAt) return '';

  const diff = Date.now() - new Date(state.lastSeenAt).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);

  if (minutes < 1) return 'только что';
  if (minutes < 60) {
    const w = pluralRu(minutes, { one: 'минуту', few: 'минуты', many: 'минут' });
    return `${minutes} ${w} назад`;
  }
  if (hours < 24) {
    const w = pluralRu(hours, { one: 'час', few: 'часа', many: 'часов' });
    return `${hours} ${w} назад`;
  }
  if (days === 1) return 'вчера';
  if (days < 7) {
    const w = pluralRu(days, { one: 'день', few: 'дня', many: 'дней' });
    return `${days} ${w} назад`;
  }
  return '';
}

export interface PresenceResult extends PresenceState {
  label: string;
}

export function usePresence(userId: string | null | undefined): PresenceResult {
  const subscribe = useCallback(
    (onChange: () => void) =>
      userId ? subscribePresence(userId, onChange) : () => {},
    [userId]
  );

  const getSnapshot = useCallback(
    () => (userId ? getPresenceSnapshot(userId) : EMPTY_PRESENCE),
    [userId]
  );

  const state = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_PRESENCE);

  return {
    ...state,
    label: formatPresenceLabel(state),
  };
}
